import '@/lib/helpers/devOnlyConsole'
import './bayawatch.ts'
import { guardRecordingAcrossNavigations } from '@/bayawatchPrivacy'
import { navigateToDeepLink as navigateToDeepLinkSafely } from '@/lib/deepLinkNavigation'
import { setupChunkErrorHandler } from '@/lib/helpers/chunkErrorHandler.ts'

// Initialize chunk error handler before anything else
// This catches 404 errors on dynamically imported modules after deployments
setupChunkErrorHandler()

// Service worker registration — hand-rolled instead of vite-plugin-pwa's
// auto-injection (injectRegister: null in vite.config.ts) so we can pass
// `updateViaCache: 'none'`. That tells the browser to revalidate the worker
// script AND its imports against the network on every update check rather than
// trusting the HTTP cache — the load-bearing fix on LOCAL/dev, where there is no
// nginx to send a no-cache header for the worker. SW generation + precaching
// still live in the VitePWA() config; only this runtime registration is manual.
//
// Dev serves a transformed ES-module worker at /dev-sw.js?dev-sw; prod serves
// the bundled classic worker at /sw.js. Registering /sw.js in dev would fetch
// the untransformed public/sw.js (bare import specifiers) and fail to register.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  const swUrl = import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/sw.js'
  // Inferred as 'module' | 'classic' (assignable to WorkerType); no explicit
  // annotation so eslint's no-undef doesn't trip on the ambient DOM type name.
  const swType = import.meta.env.DEV ? 'module' : 'classic'
  navigator.serviceWorker
    .register(swUrl, { scope: '/', type: swType, updateViaCache: 'none' })
    .catch(error => console.error('[SW] Registration failed:', error))
}

// Ask the browser to mark our origin's storage as "persistent" so it isn't
// evicted under quota pressure. With thousands of cached attachments per
// stage this matters — without persist, Chrome can clear the SW cache when
// the OS is under disk pressure and the user loses offline data silently.
// The call is idempotent and resolves with a boolean (granted/denied).
if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
  navigator.storage.persist().catch(() => {
    // Browsers gate `persist()` on engagement heuristics (installed PWA,
    // bookmark, frequent visits). Failure is fine — eviction is just more
    // aggressive in that case, not catastrophic.
  })
}

import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { onlineManager } from '@tanstack/react-query'
import {
  addListener as addNetworkListener,
  getStatus as getNetworkStatus,
} from '@/lib/platform/networkStatus'

// Mark <html> as native for CSS (prevents body scroll on native platforms)
if (Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('native-platform')

  // Wire React Query's onlineManager to Capacitor's Network plugin.
  // WKWebView's navigator.onLine / browser online/offline events are unreliable,
  // so refetchOnReconnect never fires without this.
  //
  // Both calls go through `lib/platform/networkStatus` so the dev
  // harness can override mode mid-session — flipping the NetworkPanel
  // to "offline" fires the listener below, which then flips
  // `onlineManager` to offline (and back) without needing the real
  // device network to actually change.
  onlineManager.setEventListener(setOnline => {
    const handler = addNetworkListener('networkStatusChange', status => {
      setOnline(status.connected)
    })
    getNetworkStatus().then(status => setOnline(status.connected))
    return () => {
      handler.then(h => h.remove())
    }
  })
}

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import routes from './routes/routes'
import './i18n'
import './index.css'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { queryClient } from '@/lib/api-client.ts'
import { idbPersister, pausePersist, resumePersist } from '@/lib/queryPersister.ts'
import { PERSISTED_CACHE_BUSTER, restorePersistedQueryCache } from '@/lib/persistedCacheRestore'
import {
  PERSISTED_CACHE_MAX_AGE,
  persistOptions,
  setGatedPersistedClient,
} from '@/lib/persistedCacheProvider'
import { restoreCachedAttachments } from '@/lib/prefetch/offlineAttachmentCache'
import { startPendingMarkupSync } from '@/lib/annotation/pendingMarkupSync'
import { startStartupBuildCheck } from '@/lib/helpers/startupBuildCheck'

/**
 * Initialize the app by restoring the query cache from IndexedDB BEFORE
 * mounting the router. This ensures offline data is available to loaders
 * on cold start.
 */
async function initializeApp() {
  // Markup saved in the lightbox before a reload may still be queued: read it before anything
  // renders attachments, or the first paint shows the server's old markup (usePendingMarkupStore).
  // Bounded, so a slow IndexedDB cannot hold the app back.
  const pendingMarkupReady = startPendingMarkupSync().ready
  // Web only: is the deployed build this one? The probe runs WHILE the cache restores below and
  // is settled after it, so it costs startup at most its short deadline (startupBuildCheck.ts).
  const buildCheck = startStartupBuildCheck()

  // Restore cache from IndexedDB before router loaders run
  try {
    const result = await restorePersistedQueryCache({
      queryClient,
      persister: idbPersister,
      maxAge: PERSISTED_CACHE_MAX_AGE,
      buster: PERSISTED_CACHE_BUSTER,
      pausePersist,
      resumePersist,
    })
    setGatedPersistedClient(result.gatedSnapshot)
    if (import.meta.env.DEV) {
      console.log(
        '[App] Persisted query cache restore: ' +
          `restored=${result.restored}, droppedExpired=${result.droppedExpired}, ` +
          `discarded=${result.discarded ?? 'none'}, prunedSnapshot=${result.prunedSnapshot}`,
      )
    }
  } catch (error) {
    // A failed manual gate must hydrate nothing; never fall back to the raw persisted snapshot.
    setGatedPersistedClient(undefined)
    console.error('[App] Failed to restore cache:', error)
  }

  // Restore offline attachment file URLs from Dexie (native platforms only)
  try {
    const restoredCount = await restoreCachedAttachments()
    if (import.meta.env.DEV && restoredCount > 0) {
      console.log(`[App] Restored ${restoredCount} cached attachment file URLs`)
    }
  } catch {
    // Non-critical — attachment restore failure doesn't block app startup
  }

  await Promise.race([pendingMarkupReady, new Promise(resolve => setTimeout(resolve, 1_000))])

  // A stale build with a fresh shell installed is navigating away: mounting it would only flash
  // the old UI and start work the reload throws away.
  if (await buildCheck.reloadIfStale()) return

  // Now create and mount the router with cache available
  const router = createBrowserRouter(routes)
  guardRecordingAcrossNavigations(router)

  // Native push tap → deep-link into the issue. This listener runs in the app
  // context (not a separate service worker), so an in-app router navigation is
  // reliable here. Web tap handling lives in firebase-messaging-sw.js.
  if (Capacitor.isNativePlatform()) {
    // Waits for the router's initial load before navigating, and stops session
    // recording first when the target is a token route — see the module.
    const navigateToDeepLink = (path: string) => navigateToDeepLinkSafely(router, path)
    // We handle the notification TAP (deep-link) only — deliberately NOT a
    // `notificationReceived` (foreground-arrival) listener. FCM is the
    // BACKGROUND channel: the OS auto-displays its `notification` payload when
    // the app is backgrounded/killed. In the FOREGROUND, real-time alerts
    // already arrive via Mercure SSE (useNotificationsSubscriber, mounted
    // app-wide on every platform), which shows the in-app toast. Adding a
    // foreground FCM display here would double-notify (Mercure toast + FCM
    // banner) for the same event — so don't.
    void FirebaseMessaging.addListener('notificationActionPerformed', event => {
      const data = event.notification?.data as { url?: unknown } | undefined
      const url = data?.url
      if (typeof url !== 'string') return
      try {
        const target = new URL(url, window.location.origin)
        navigateToDeepLink(target.pathname + target.search + target.hash)
      } catch {
        // ignore a malformed URL
      }
    })

    // Universal Links (iOS) / App Links (Android): an https link to an issue —
    // e.g. tapped from an email notification — opens the app here instead of
    // the browser (see the AASA / assetlinks.json files + the app's
    // associated-domains entitlement / autoVerify intent-filter), then we
    // navigate to the link's path, deferred until the router is ready (same
    // cold-start reasoning as the push deep-link above).
    const openDeepLinkUrl = (rawUrl: string) => {
      try {
        const target = new URL(rawUrl)
        // Only act on our own associated domains — ignore the plain
        // `capacitor://localhost` launch URL (normal icon launch) and anything
        // else, so a non-link cold start doesn't get force-navigated.
        if (target.hostname !== 'buildlog.app' && !target.hostname.endsWith('.buildlog.app')) return
        navigateToDeepLink(target.pathname + target.search + target.hash)
      } catch {
        // ignore a malformed URL
      }
    }
    // COLD start: the launching link is NOT delivered through `appUrlOpen` —
    // that event only fires for links received while the app is already
    // running. On a fresh launch the URL is available ONLY via getLaunchUrl().
    // Verified on the Android emulator: without this, a cold app-link tap booted
    // to the default route instead of the deep-linked issue (warm taps worked).
    void CapacitorApp.getLaunchUrl().then(res => {
      if (res?.url) openDeepLinkUrl(res.url)
    })
    // WARM: links received while the app is already running.
    void CapacitorApp.addListener('appUrlOpen', event => openDeepLinkUrl(event.url))
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <RouterProvider router={router} />
      </PersistQueryClientProvider>
    </StrictMode>,
  )
}

initializeApp()

// Dev harness — separate React root mounted ONLY when `VITE_ENABLE_DEV_HARNESS=true`
// at build time. The `if (__DEV_HARNESS__)` form (compile-time boolean from the
// Vite `define`) is the DCE-friendly shape: when the flag is false, Rollup
// strips this whole branch + the dynamic-import target. Verified by the
// `verify-prod-build` CI step.
//
// Why a separate root and not a child of the app's React tree: keeps the
// harness's React lifecycle independent of router/QueryClient transitions,
// and the panels don't fight the app's z-index / overflow constraints.
if (__DEV_HARNESS__) {
  void import('@/lib/devtools/harness/HarnessRoot').then(({ mountHarness }) => {
    mountHarness()
  })
}
